import { inject, Injectable } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { classifyAuthError } from '../error-handling/auth-error-handler';
import { AuthSession } from '../models/auth.model';
import { AuthStore } from '../stores/auth-store';

type TokenSelector = (session: AuthSession) => string | undefined;

@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private readonly _authStore = inject(AuthStore);

  private _refreshInFlight$: Observable<AuthSession> | null = null;

  getValidToken$(): Observable<string> {
    return this._getTokenFromSession$(
      false,
      (session) => session.tokens?.accessToken?.toString(),
      'No access token available'
    );
  }

  getValidIdToken$(): Observable<string> {
    return this._getTokenFromSession$(
      false,
      (session) => session.tokens?.idToken?.toString(),
      'No id token available'
    );
  }

  forceRefresh$(): Observable<string> {
    return this._forceRefreshSession$().pipe(
      map((session) => {
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          throw new Error('Token refresh failed - no token returned');
        }
        return token;
      })
    );
  }

  forceRefreshIdToken$(): Observable<string> {
    return this._forceRefreshSession$().pipe(
      map((session) => {
        const token = session.tokens?.idToken?.toString();
        if (!token) {
          throw new Error('Id token refresh failed - no token returned');
        }
        return token;
      })
    );
  }

  getAccessToken(): string | null {
    const session = this._authStore.getSessionOrNull();
    return session?.tokens?.accessToken?.toString() ?? null;
  }

  getIdToken(): string | null {
    const session = this._authStore.getSessionOrNull();
    return session?.tokens?.idToken?.toString() ?? null;
  }

  /** Email claim from the cached idToken payload, when available. */
  getIdTokenEmail(): string | null {
    const session = this._authStore.getSessionOrNull();
    const payload = session?.tokens?.idToken?.payload;
    if (payload && typeof payload === 'object' && 'email' in payload) {
      const email = payload['email'];
      if (typeof email === 'string' && email.trim()) {
        return email.toLowerCase().trim();
      }
    }
    return null;
  }

  isAuthenticated$(): Observable<boolean> {
    return this._fetchSession$(false).pipe(
      map(
        (session) => session.isAuthenticated && !!session.tokens?.accessToken
      ),
      catchError(() => of(false))
    );
  }

  clearPendingRefresh(): void {
    this._refreshInFlight$ = null;
  }

  private _getTokenFromSession$(
    forceRefresh: boolean,
    selector: TokenSelector,
    missingMessage: string
  ): Observable<string> {
    if (this._refreshInFlight$) {
      return this._refreshInFlight$.pipe(
        map((session) => {
          const token = selector(session);
          if (!token) {
            throw new Error(missingMessage);
          }
          return token;
        })
      );
    }

    return this._fetchSession$(forceRefresh).pipe(
      map((session) => {
        const token = selector(session);
        if (!token) {
          throw new Error(missingMessage);
        }
        return token;
      })
    );
  }

  private _forceRefreshSession$(): Observable<AuthSession> {
    if (this._refreshInFlight$) {
      return this._refreshInFlight$;
    }

    this._authStore.setRefreshInProgress(true);
    this._authStore.setTokenStatus('refreshing');

    this._refreshInFlight$ = this._fetchSession$(true).pipe(
      tap(() => {
        this._authStore.setTokenStatus('valid');
      }),
      catchError((error) => {
        const authError = classifyAuthError(error);
        this._authStore.setLastRefreshError(authError);
        this._authStore.setTokenStatus('invalid');
        return throwError(() => authError);
      }),
      finalize(() => {
        this._refreshInFlight$ = null;
        this._authStore.setRefreshInProgress(false);
      }),
      shareReplay(1)
    );

    return this._refreshInFlight$;
  }

  private _fetchSession$(forceRefresh: boolean): Observable<AuthSession> {
    return from(fetchAuthSession({ forceRefresh })).pipe(
      map((cognitoSession) => {
        const session: AuthSession = {
          tokens: cognitoSession.tokens ?? null,
          isAuthenticated: !!cognitoSession.tokens?.accessToken,
          lastFetched: Date.now(),
        };

        this._authStore.updateSession(session);

        return session;
      })
    );
  }
}
